import React, { useState } from 'react';
import Box from '@mui/material/Box';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DrawerV2, ButtonV2, ChipV2, DatePicker, dsV2 } from '@bloomays-lib/ui.shared';
import type { BrowserUploadFile, ISociety } from '@bloomays-lib/types.shared';
import { UPSERT_BLOOMER_SOCIETY, UPSERT_BLOOMER, UPLOAD_AMENDMENT } from '@bloomays-lib/adapter.api-bloomer';
import { dateFormat } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { deriveDocumentStatus, type DocumentStatus } from '../../../../helpers/missionDashboardV2';
import { getSocietyDocExpirationConstraints, type SocietyDocKind } from '../../../../helpers/societyForm';
import { Upload } from '../../../molecules/Upload';
import { FieldV2, InfoV2 } from '../formFields';
import { DocPreviewModal } from './DocPreviewModal';

type DocContract = { recordId?: string; type?: string; status?: string; signedDate?: string; signedFileUrl?: string };

export type DocumentsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  missionRecordId: string;
  bloomerSociety?: ISociety;
  bloomer?: { recordId?: string; firstname?: string; lastname?: string; email?: string; credentialID?: string };
  contracts?: DocContract[];
};

const STATUS_TONE: Record<DocumentStatus, { bg: string; fg: string; chip: 'success' | 'warning' | 'error' | 'info' }> =
  {
    valid: { bg: dsV2.color.vertDim, fg: dsV2.color.vert, chip: 'success' },
    warn: { bg: dsV2.color.amberDim, fg: dsV2.color.amber, chip: 'warning' },
    expired: { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge, chip: 'error' },
    missing: { bg: dsV2.color.rougeDim, fg: dsV2.color.rouge, chip: 'error' },
    pending: { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu, chip: 'info' },
  };

/** Ligne d'upload d'un document (`.docm`) : zone drag-and-drop PDF + date d'expiration + enregistrement. */
const DocUploadRow = ({
  label,
  icon,
  optional,
  currentUrl,
  initialExpiration,
  hasExpiration,
  expirationKind,
  saving,
  onSave,
  onPreview,
}: {
  label: string;
  icon: React.ReactNode;
  optional?: boolean;
  currentUrl?: string;
  initialExpiration?: string;
  hasExpiration: boolean;
  /** Type de document (KBIS / URSSAF / RCP) — borne les dates d'expiration sélectionnables (règle mutualisée). */
  expirationKind?: SocietyDocKind;
  saving: boolean;
  onSave: (args: { file: BrowserUploadFile | null; expiration?: string }) => void;
  onPreview?: (url: string, title: string) => void;
}) => {
  const { t } = useTranslation(['missionDashboard']);
  const [file, setFile] = useState<BrowserUploadFile | null>(null);
  const [expiration, setExpiration] = useState<string>(initialExpiration ?? '');

  const uploaded = Boolean(currentUrl) || Boolean(file);
  const status = deriveDocumentStatus({
    uploaded,
    requiresExpiration: hasExpiration,
    expirationDate: hasExpiration ? expiration || undefined : undefined,
  });
  const STATUS_LABEL: Record<DocumentStatus, string> = {
    valid: t('drawers.documents.statusLabel.valid', { ns: 'missionDashboard' }),
    warn: t('drawers.documents.statusLabel.warn', { ns: 'missionDashboard' }),
    expired: t('drawers.documents.statusLabel.expired', { ns: 'missionDashboard' }),
    missing: t('drawers.documents.statusLabel.missing', { ns: 'missionDashboard' }),
    pending: t('drawers.documents.statusLabel.pending', { ns: 'missionDashboard' }),
  };
  const tone = STATUS_TONE[status];
  const dirty = file !== null || (hasExpiration && (expiration || '') !== (initialExpiration ?? ''));

  return (
    <Box
      sx={{
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.sm,
        padding: '12px 13px',
        marginBottom: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: tone.bg,
            color: tone.fg,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
            {label}
            {optional ? t('drawers.documents.optionalSuffix', { ns: 'missionDashboard' }) : ''}
          </Box>
          {file?.path ? (
            <Box sx={{ fontSize: 11, color: dsV2.color.t4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.path}
            </Box>
          ) : null}
        </Box>
        <ChipV2 label={STATUS_LABEL[status]} tone={tone.chip} size="small" />
      </Box>

      {/* Contrôle de dépôt compact (Dropzone slim) : autoload remonte le fichier sélectionné,
          aperçu inline (œil) du document courant, état loading pendant l'enregistrement. */}
      <Upload
        id={`docup-${label.replace(/\s+/g, '-').toLowerCase()}`}
        noModal
        autoload
        showFiles={false}
        isLoading={saving}
        textLabel={t('drawers.documents.chooseFile', { ns: 'missionDashboard' })}
        placeholder={t('drawers.documents.dropzonePlaceholder', { ns: 'missionDashboard' })}
        maxFiles={1}
        maxLength={20000000}
        fileTypes={['.pdf']}
        preloadedFiles={[]}
        previewUrl={currentUrl}
        onPreview={currentUrl && onPreview ? () => onPreview(currentUrl, label) : undefined}
        handleChange={(accepted) => setFile(accepted[0] ?? null)}
      />

      {hasExpiration ? (
        <Box sx={{ marginTop: '10px' }}>
          <FieldV2 label={t('drawers.documents.expirationDate', { ns: 'missionDashboard' })}>
            {/* DatePicker partagé (variante small) + bornes mutualisées avec EditBloomerSociety :
                impossible de choisir une date passée ou hors fenêtre métier (KBIS 3m / URSSAF 6m / RCP 12m). */}
            <DatePicker
              size="small"
              fullWidth
              ariaLabel={`expiration-${expirationKind ?? 'doc'}`}
              value={expiration ? new Date(expiration) : null}
              handleChange={(date) => setExpiration(date ? dateFormat(date, 'yyyy-MM-dd') : '')}
              disabled={saving}
              {...(expirationKind ? getSocietyDocExpirationConstraints(expirationKind) : {})}
            />
          </FieldV2>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
        <ButtonV2
          size="small"
          disabled={!dirty || saving}
          loading={saving}
          onClick={() => onSave({ file, expiration })}
        >
          {t('drawers.documents.save', { ns: 'missionDashboard' })}
        </ButtonV2>
      </Box>
    </Box>
  );
};

/** Ligne contrat en lecture seule (PDS/SST) — la signature passe par le parcours contrats. */
const ContractRow = ({
  contract,
  missionRecordId,
  onSaved,
  onPreview,
}: {
  contract: DocContract;
  missionRecordId: string;
  onSaved: () => void;
  onPreview?: (url: string, title: string) => void;
}) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);
  const signed = Boolean(contract.signedDate || contract.signedFileUrl);
  const tone = signed ? STATUS_TONE.valid : STATUS_TONE.warn;
  const [file, setFile] = useState<BrowserUploadFile | null>(null);
  const [signatureDate, setSignatureDate] = useState<string>('');
  const [uploadAmendment, { loading: uploading }] = useMutation(UPLOAD_AMENDMENT);

  // Tant que le contrat n'est pas signé (signature en cours), on permet d'uploader le PDF signé + sa
  // date de signature — même flux que le parcours contrats (mutation `uploadAmendment`, signed=true).
  const canUpload = !signed && Boolean(contract.recordId);
  const canSubmit = canUpload && Boolean(file) && Boolean(signatureDate);

  const submit = async () => {
    if (!file || !signatureDate || !contract.recordId) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await uploadAmendment({
        variables: {
          input: {
            file,
            legalContractRecordId: contract.recordId,
            missionRecordId,
            signed: true,
            createInvitation: false,
            signedDate: signatureDate,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.sm,
        padding: '11px 13px',
        marginBottom: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: tone.bg,
            color: tone.fg,
          }}
        >
          <DescriptionRoundedIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
            {t('drawers.documents.contractPrefix', { ns: 'missionDashboard', type: contract.type })}
          </Box>
          <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
            {signed && contract.signedDate
              ? t('drawers.documents.signedOn', {
                  ns: 'missionDashboard',
                  date: new Date(contract.signedDate).toLocaleDateString('fr-FR'),
                })
              : t('drawers.documents.awaitingSignature', { ns: 'missionDashboard' })}
          </Box>
        </Box>
        {contract.signedFileUrl && onPreview ? (
          <Box
            component="button"
            type="button"
            onClick={() =>
              onPreview(
                contract.signedFileUrl as string,
                t('drawers.documents.contractPrefix', { ns: 'missionDashboard', type: contract.type ?? '' }),
              )
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: 12,
              color: dsV2.color.bleu,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <VisibilityRoundedIcon sx={{ fontSize: 15 }} /> {t('drawers.documents.view', { ns: 'missionDashboard' })}
          </Box>
        ) : null}
      </Box>

      {/* Régularisation : upload du contrat signé + date de signature tant qu'il n'est pas signé. */}
      {canUpload ? (
        <Box sx={{ marginTop: '12px' }}>
          <Upload
            id={`contract-${(contract.type ?? 'contract').toLowerCase()}`}
            noModal
            autoload
            showFiles={false}
            isLoading={uploading}
            textLabel={t('drawers.documents.chooseFile', { ns: 'missionDashboard' })}
            placeholder={t('drawers.documents.dropzonePlaceholder', { ns: 'missionDashboard' })}
            maxFiles={1}
            maxLength={20000000}
            fileTypes={['.pdf']}
            preloadedFiles={[]}
            handleChange={(accepted) => setFile(accepted[0] ?? null)}
          />
          <Box sx={{ marginTop: '10px' }}>
            <FieldV2 label={t('drawers.documents.signatureDate', { ns: 'missionDashboard' })}>
              <DatePicker
                size="small"
                fullWidth
                ariaLabel={`signature-${contract.type ?? 'contract'}`}
                value={signatureDate ? new Date(signatureDate) : null}
                handleChange={(date) => setSignatureDate(date ? dateFormat(date, 'yyyy-MM-dd') : '')}
                maxDate={new Date()}
                disabled={uploading}
              />
            </FieldV2>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
            <ButtonV2 size="small" disabled={!canSubmit || uploading} loading={uploading} onClick={submit}>
              {t('drawers.documents.save', { ns: 'missionDashboard' })}
            </ButtonV2>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * Gestion des documents en panneau latéral (DrawerV2). Upload / remplacement des attestations de la
 * société du Bloomer (KBIS, URSSAF, RCP) via `UPSERT_BLOOMER_SOCIETY` et de la carte d'identité via
 * `UPSERT_BLOOMER`. Les contrats (PDS / SST) sont affichés en lecture seule — la signature passe par
 * le parcours contrats dédié.
 */
export const DocumentsDrawer = ({
  open,
  onClose,
  onSaved,
  missionRecordId,
  bloomerSociety,
  bloomer,
  contracts,
}: DocumentsDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);
  const [upsertBloomerSociety, { loading: savingSociety }] = useMutation(UPSERT_BLOOMER_SOCIETY);
  const [upsertBloomer, { loading: savingBloomer }] = useMutation(UPSERT_BLOOMER);
  const [savingKind, setSavingKind] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const handlePreview = (url: string, title: string) => setPreview({ url, title });

  const saveSocietyDoc = async (
    kind: string,
    fileField: string,
    dateField: string,
    { file, expiration }: { file: BrowserUploadFile | null; expiration?: string },
  ) => {
    if (!bloomerSociety?.recordId) return;
    const input: Record<string, unknown> = { recordId: bloomerSociety.recordId };
    if (file) input[fileField] = file;
    if (expiration) input[dateField] = expiration;
    setSavingKind(kind);
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomerSociety({ variables: { input } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setSavingKind(null);
    }
  };

  const saveCI = async ({ file }: { file: BrowserUploadFile | null }) => {
    if (!bloomer?.recordId || !file) return;
    setSavingKind('ci');
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomer({
        variables: {
          input: {
            recordId: bloomer.recordId,
            firstname: bloomer.firstname,
            lastname: bloomer.lastname,
            email: bloomer.email,
            credentialID: file,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setSavingKind(null);
    }
  };

  const pds = contracts?.find((c) => c.type === 'PDS');
  const sst = contracts?.find((c) => c.type === 'SST');

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('drawers.documents.title', { ns: 'missionDashboard' })}
      subtitle={
        bloomerSociety?.name
          ? t('drawers.documents.subtitleWithSociety', { ns: 'missionDashboard', name: bloomerSociety.name })
          : t('drawers.documents.subtitle', { ns: 'missionDashboard' })
      }
      width={480}
      footer={<ButtonV2 onClick={onClose}>{t('drawers.common.close', { ns: 'missionDashboard' })}</ButtonV2>}
    >
      <InfoV2 tone="amber" icon={<ScheduleRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('drawers.documents.intro', { ns: 'missionDashboard' })}
      </InfoV2>

      {pds ? (
        <ContractRow contract={pds} missionRecordId={missionRecordId} onSaved={onSaved} onPreview={handlePreview} />
      ) : null}
      {sst ? (
        <ContractRow contract={sst} missionRecordId={missionRecordId} onSaved={onSaved} onPreview={handlePreview} />
      ) : null}

      <DocUploadRow
        label={t('drawers.documents.ci', { ns: 'missionDashboard' })}
        icon={<BadgeRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomer?.credentialID}
        hasExpiration={false}
        saving={savingBloomer && savingKind === 'ci'}
        onSave={saveCI}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label={t('drawers.documents.kbis', { ns: 'missionDashboard' })}
        icon={<AccountBalanceRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomerSociety?.KBIS}
        initialExpiration={bloomerSociety?.KbisExpirationDate}
        hasExpiration
        expirationKind="kbis"
        saving={savingSociety && savingKind === 'kbis'}
        onSave={(args) => saveSocietyDoc('kbis', 'KBIS', 'KbisExpirationDate', args)}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label={t('drawers.documents.urssaf', { ns: 'missionDashboard' })}
        icon={<VerifiedRoundedIcon sx={{ fontSize: 17 }} />}
        currentUrl={bloomerSociety?.URSSAFVigilanceCertificate}
        initialExpiration={bloomerSociety?.URSSAFVCExpirationDate}
        hasExpiration
        expirationKind="urssaf"
        saving={savingSociety && savingKind === 'urssaf'}
        onSave={(args) => saveSocietyDoc('urssaf', 'URSSAFVigilanceCertificate', 'URSSAFVCExpirationDate', args)}
        onPreview={handlePreview}
      />
      <DocUploadRow
        label={t('drawers.documents.rcp', { ns: 'missionDashboard' })}
        icon={<ShieldRoundedIcon sx={{ fontSize: 17 }} />}
        optional
        currentUrl={bloomerSociety?.RCPVigilanceCertificate}
        initialExpiration={bloomerSociety?.RCPExpirationDate}
        hasExpiration
        expirationKind="rcp"
        saving={savingSociety && savingKind === 'rcp'}
        onSave={(args) => saveSocietyDoc('rcp', 'RCPVigilanceCertificate', 'RCPExpirationDate', args)}
        onPreview={handlePreview}
      />

      {preview ? (
        <DocPreviewModal open url={preview.url} title={preview.title} onClose={() => setPreview(null)} />
      ) : null}
    </DrawerV2>
  );
};

export default DocumentsDrawer;
